import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DEPGUARD_PRODUCT, VIGIL_PRODUCT, PRISM_PRODUCT } from './websiteData';

const PRODUCTS = [DEPGUARD_PRODUCT, VIGIL_PRODUCT, PRISM_PRODUCT];

function FeatureGrid({ features, accentColor }) {
  return (
    <div className="dgw-feature-grid">
      {features.map(f => (
        <div key={f.title} className="dgw-feature-item" style={{'--hover-color': accentColor}}>
          <div className="dgw-feature-item-icon">{f.icon}</div>
          <div className="dgw-feature-item-title">{f.title}</div>
          <div className="dgw-feature-item-desc">{f.desc}</div>
        </div>
      ))}
    </div>
  );
}

function UseCases({ useCases }) {
  if (!useCases || useCases.length === 0) return null;
  return (
    <div className="dgw-grid-2">
      {useCases.map(u => (
        <div key={u.title} className="dgw-card">
          {u.icon && <div style={{fontSize:24, marginBottom:8}}>{u.icon}</div>}
          <h4 style={{color:'#1e293b', marginBottom:8, fontSize:15}}>{u.title}</h4>
          <p style={{color:'#64748b', fontSize:14, lineHeight:1.6, margin:0}}>{u.desc}</p>
        </div>
      ))}
    </div>
  );
}

function ProductTab({ product }) {
  const { id, name, icon, accentColor, tagline, heroTitle, heroLead, features, useCases } = product;
  return (
    <div>
      {/* Product hero strip */}
      <div className="dgw-2col" style={{
        padding: '48px 0 40px',
        borderBottom: '1px solid #e2e8f0',
        marginBottom: 48,
        gap: 48,
      }}>
        <div>
          <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:20}}>
            <span style={{fontSize:32}}>{icon}</span>
            <div>
              <div style={{fontSize:12, fontWeight:700, letterSpacing:'.6px', textTransform:'uppercase', color: accentColor, marginBottom:2}}>{tagline}</div>
              <div style={{fontSize:24, fontWeight:800, color:'#1e293b', letterSpacing:'-.5px'}}>{name}</div>
            </div>
          </div>
          <h2 style={{fontSize:'clamp(24px,3vw,40px)', fontWeight:800, letterSpacing:'-1px', color:'#1e293b', marginBottom:16}}>{heroTitle}</h2>
          <p style={{fontSize:16, color:'#64748b', lineHeight:1.7, marginBottom:24}}>{heroLead}</p>
          <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
            <Link to={`/trial?plan=professional&product=${id}`} className="dgw-btn dgw-btn-primary">
              Start free trial →
            </Link>
            <Link to="/contact?type=demo" className="dgw-btn dgw-btn-ghost">Book demo</Link>
          </div>
        </div>
        {/* Accent panel */}
        <div style={{
          padding: 32,
          borderRadius: 16,
          background: `rgba(${product.accentRgb},.06)`,
          border: `1px solid rgba(${product.accentRgb},.2)`,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}>
          {features.slice(0,3).map(f => (
            <div key={f.title} style={{display:'flex', gap:12, alignItems:'flex-start'}}>
              <span style={{fontSize:18, flexShrink:0}}>{f.icon}</span>
              <div>
                <div style={{fontSize:14, fontWeight:700, color:'#1e293b', marginBottom:2}}>{f.title}</div>
                <div style={{fontSize:13, color:'#64748b', lineHeight:1.5}}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* All features */}
      <div style={{marginBottom:64}}>
        <h3 style={{fontSize:22, fontWeight:800, color:'#1e293b', marginBottom:32, letterSpacing:'-.3px'}}>All capabilities</h3>
        <FeatureGrid features={features} accentColor={accentColor} />
      </div>

      {/* Use cases */}
      <div>
        <h3 style={{fontSize:22, fontWeight:800, color:'#1e293b', marginBottom:32, letterSpacing:'-.3px'}}>Use cases</h3>
        <UseCases useCases={useCases} />
      </div>
    </div>
  );
}

export default function WebsiteProductPage({ product: productProp }) {
  const { product: productParam } = useParams();
  const initialId = productProp || productParam || 'depguard';
  const [activeId, setActiveId] = useState(initialId);
  const active = PRODUCTS.find(p => p.id === activeId) || PRODUCTS[0];

  return (
    <div className="dgw-page-wrap">
      <div style={{maxWidth:1280, margin:'0 auto', padding:'60px clamp(20px,5vw,60px)'}}>
        {/* Page header */}
        <div style={{marginBottom:48, textAlign:'center'}}>
          <span className="dgw-section-tag">Products</span>
          <h1 style={{fontSize:'clamp(32px,5vw,60px)', fontWeight:800, color:'#1e293b', letterSpacing:'-1.5px', marginBottom:16}}>
            Three products. One platform.
          </h1>
          <p style={{fontSize:18, color:'#64748b', maxWidth:560, margin:'0 auto'}}>
            Each product is built on the PRISM Code Property Graph engine.
            Same data. Same speed. Different lens.
          </p>
        </div>

        {/* Tabs */}
        <div style={{display:'flex', justifyContent:'center', marginBottom:48}}>
          <div className="dgw-tabs">
            {PRODUCTS.map(p => (
              <button
                key={p.id}
                className={`dgw-tab${activeId === p.id ? ' active' : ''}`}
                onClick={() => setActiveId(p.id)}
                style={activeId === p.id ? {color: p.accentColor} : {}}
              >
                {p.icon} {p.name}
              </button>
            ))}
          </div>
        </div>

        {/* Active product */}
        <ProductTab key={active.id} product={active} />
      </div>
    </div>
  );
}
